import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useAuth } from '../context/AuthContext';
import adminAPI from '../../services/adminAPI';
import '../../styles/admin/AccountStatement.css';
import { useDesktopViewport } from '../../hooks/useDesktopViewport';

const AccountStatement = () => {
    useDesktopViewport();
    // ---- Get user from localStorage to check role ----
    const getUserFromStorage = () => {
        try {
            return JSON.parse(localStorage.getItem('user') || '{}');
        } catch {
            return {};
        }
    };
    const userData = getUserFromStorage();
    const isAdmin = userData?.role === 'admin';   // ← boolean for access control
    
    const { user } = useAuth();
    const [allTransactions, setAllTransactions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedTransaction, setSelectedTransaction] = useState(null);
    const [showBetModal, setShowBetModal] = useState(false);
    
    // ============ NEW: Unsettle States ============
    const [showUnsettleModal, setShowUnsettleModal] = useState(false);
    const [unsettlingBet, setUnsettlingBet] = useState(null);
    const [unsettleReason, setUnsettleReason] = useState('');
    const [unsettling, setUnsettling] = useState(false);
    const [toast, setToast] = useState({ show: false, message: '', type: 'success' });
    
    const [filters, setFilters] = useState({
        accountType: 'all',
        transactionType: 'all',
        searchClient: '',
        fromDate: '',
        toDate: ''
    });
    const [pagination, setPagination] = useState({
        currentPage: 1,
        totalPages: 1,
        totalItems: 0,
        itemsPerPage: 100
    });
    
    const initialLoadRef = useRef(true);

    // ============ NEW: Show Toast ============
    const showToast = (message, type = 'success') => {
        setToast({ show: true, message, type });
        setTimeout(() => {
            setToast({ show: false, message: '', type: 'success' });
        }, 4000);
    };

    // ============ NEW: Open Unsettle Modal ============
// ============ NEW: Open Unsettle Modal ============
const handleUnsettleClick = (transaction) => {
    // Only allow unsettle if bet has betDetails and a result
    if (!transaction.betDetails || !transaction.betDetails.result) {
        showToast('This bet is not settled', 'error');
        return;
    }
    
    // Close the bet details modal first if it's open
    if (showBetModal) {
        setShowBetModal(false);
    }
    
    setUnsettlingBet(transaction);
    setUnsettleReason('');
    setShowUnsettleModal(true);
};

    // ============ NEW: Confirm Unsettle ============
// ============ NEW: Confirm Unsettle ============
const handleConfirmUnsettle = async () => {
    // Get bet ID from transaction.id (primary) or from betDetails.betId (fallback)
    const betId = unsettlingBet?.id || unsettlingBet?.betDetails?.betId;
    
    if (!betId) {
        showToast('Bet ID not found', 'error');
        return;
    }
    
    setUnsettling(true);
    
    try {
        const response = await adminAPI.unsettleBet({
            betId: betId,
            adminId: user?._id || user?.id,
            reason: unsettleReason || 'Manual unsettle from Account Statement'
        });
        
        // Close modals immediately on success
        if (response?.success || response?.data?.success) {
            showToast(`✅ Bet ${betId} unsettled successfully!`, 'success');
            
            // Close both modals
            setShowUnsettleModal(false);
            setShowBetModal(false);
            setUnsettlingBet(null);
            
            // Reload transactions after a small delay
            setTimeout(() => {
                loadTransactions();
            }, 500);
        } else {
            showToast(response?.data?.message || response?.message || 'Failed to unsettle bet', 'error');
        }
    } catch (error) {
        console.error('Error unsettling bet:', error);
        showToast(error.response?.data?.message || error.message || 'Failed to unsettle bet', 'error');
    } finally {
        setUnsettling(false);
    }
};

    // Process transactions to calculate USER-SPECIFIC running balance
    const processedTransactions = useMemo(() => {
        const userTransactions = {};
        
        allTransactions.forEach(transaction => {
            const userId = transaction.userId || transaction.username;
            if (!userTransactions[userId]) {
                userTransactions[userId] = [];
            }
            userTransactions[userId].push(transaction);
        });
        
        const processed = [];
        
        Object.values(userTransactions).forEach(userTxns => {
            const sorted = [...userTxns].sort((a, b) => 
                new Date(a.date) - new Date(b.date)
            );
            
            let runningBalance = 0;
            
            sorted.forEach(transaction => {
                runningBalance += transaction.amount;
                
                processed.push({
                    ...transaction,
                    runningBalance: runningBalance
                });
            });
        });
        
        return processed.sort((a, b) => new Date(b.date) - new Date(a.date));
    }, [allTransactions]);

    const filteredTransactions = useMemo(() => {
        let filtered = [...processedTransactions];
        
        if (filters.searchClient && filters.searchClient.trim() !== '') {
            const searchTerm = filters.searchClient.toLowerCase().trim();
            filtered = filtered.filter(transaction => 
                transaction.username && transaction.username.toLowerCase().includes(searchTerm)
            );
        }
        
        if (filters.accountType && filters.accountType !== 'all') {
            filtered = filtered.filter(transaction => 
                transaction.userRole === filters.accountType
            );
        }
        
        if (filters.transactionType && filters.transactionType !== 'all') {
            filtered = filtered.filter(transaction => 
                transaction.type === filters.transactionType
            );
        }
        
        if (filters.fromDate) {
            const fromDate = new Date(filters.fromDate);
            fromDate.setHours(0, 0, 0, 0);
            filtered = filtered.filter(transaction => 
                new Date(transaction.date) >= fromDate
            );
        }
        
        if (filters.toDate) {
            const toDate = new Date(filters.toDate);
            toDate.setHours(23, 59, 59, 999);
            filtered = filtered.filter(transaction => 
                new Date(transaction.date) <= toDate
            );
        }
        
        return filtered;
    }, [processedTransactions, filters]);

    const filteredSummary = useMemo(() => {
        const summary = {
            totalDeposits: 0,
            totalWithdrawals: 0,
            totalWins: 0,
            totalLosses: 0,
            netProfit: 0,
            closingBalance: 0
        };
        
        const uniqueUsers = new Set();
        const userLatestBalances = {};
        
        filteredTransactions.forEach(transaction => {
            if (transaction.type === 'deposit') {
                summary.totalDeposits += Math.abs(transaction.amount);
            } else if (transaction.type === 'withdrawal') {
                summary.totalWithdrawals += Math.abs(transaction.amount);
            } else if (transaction.type === 'win') {
                summary.totalWins += Math.abs(transaction.amount);
            } else if (transaction.type === 'loss') {
                summary.totalLosses += Math.abs(transaction.amount);
            }
            
            const userId = transaction.userId || transaction.username;
            uniqueUsers.add(userId);
            userLatestBalances[userId] = transaction.runningBalance;
        });
        
        summary.netProfit = summary.totalWins - summary.totalLosses;
        summary.closingBalance = Object.values(userLatestBalances).reduce((sum, bal) => sum + bal, 0);
        
        return summary;
    }, [filteredTransactions]);

    const paginatedTransactions = useMemo(() => {
        const startIndex = (pagination.currentPage - 1) * pagination.itemsPerPage;
        const endIndex = startIndex + pagination.itemsPerPage;
        return filteredTransactions.slice(startIndex, endIndex);
    }, [filteredTransactions, pagination.currentPage, pagination.itemsPerPage]);

    useEffect(() => {
        const totalPages = Math.ceil(filteredTransactions.length / pagination.itemsPerPage);
        setPagination(prev => ({
            ...prev,
            totalPages: totalPages || 1,
            totalItems: filteredTransactions.length,
            currentPage: 1
        }));
    }, [filteredTransactions.length, pagination.itemsPerPage]);

    const loadTransactions = useCallback(async () => {
        try {
            setLoading(true);
            
            const params = {
                limit: 10000,
                fromDate: filters.fromDate || undefined,
                toDate: filters.toDate || undefined
            };
            
            Object.keys(params).forEach(key => {
                if (params[key] === undefined || params[key] === '') {
                    delete params[key];
                }
            });
            
            const response = await adminAPI.getAccountStatement(params);
            const transactions = response.data.transactions || [];
            setAllTransactions(transactions);
            
        } catch (error) {
            console.error('Error loading transactions:', error);
            setAllTransactions([]);
        } finally {
            setLoading(false);
            initialLoadRef.current = false;
        }
    }, [filters.fromDate, filters.toDate]);

    useEffect(() => {
        loadTransactions();
    }, []);

    const handleFilterChange = (key, value) => {
        setFilters(prev => ({ ...prev, [key]: value }));
    };

    const handleReset = () => {
        setFilters({
            accountType: 'all',
            transactionType: 'all',
            searchClient: '',
            fromDate: '',
            toDate: ''
        });
        setPagination(prev => ({ ...prev, currentPage: 1 }));
    };

    const handlePageChange = (newPage) => {
        setPagination(prev => ({ ...prev, currentPage: newPage }));
    };

    const handleTransactionClick = (transaction) => {
        setSelectedTransaction(transaction);
        setShowBetModal(true);
    };

    const formatCurrency = (amount) => {
        if (!amount && amount !== 0) return '₹0';
        return `₹${amount.toLocaleString('en-IN')}`;
    };

    const formatDate = (date) => {
        return new Date(date).toLocaleString('en-IN', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit'
        });
    };

    const getBetTypeDisplay = (betDetails) => {
        if (!betDetails) return null;
        
        const { marketType, betType } = betDetails;
        
        if (marketType === 'session') {
            return betType === 'yes' ? 'YES' : 'NO';
        } else if (marketType === 'bookmaker' || marketType === 'bookmaker2' || marketType === 'bookmaker_tied') {
            return betType === 'back' ? 'BACK' : 'LAY';
        }
        return null;
    };

    const getRowColorClass = (transaction) => {
        if (!transaction.betDetails) return '';
        
        const { marketType, betType } = transaction.betDetails;
        
        if (marketType === 'session') {
            return betType === 'yes' ? 'row-yes' : 'row-no';
        } else if (marketType === 'bookmaker' || marketType === 'bookmaker2' || marketType === 'bookmaker_tied') {
            return betType === 'back' ? 'row-back' : 'row-lay';
        }
        return '';
    };

    const getBetTypeClass = (betDetails) => {
        if (!betDetails) return '';
        
        const { marketType, betType } = betDetails;
        
        if (marketType === 'session') {
            return betType === 'yes' ? 'bet-type-yes' : 'bet-type-no';
        } else if (marketType === 'bookmaker' || marketType === 'bookmaker2' || marketType === 'bookmaker_tied') {
            return betType === 'back' ? 'bet-type-back' : 'bet-type-lay';
        }
        return 'bet-type-na';
    };

    if (loading && allTransactions.length === 0) {
        return <div className="loading">Loading transactions...</div>;
    }

    return (
        <div className="account-statement">
            {/* ============ NEW: Toast Notification ============ */}
            {toast.show && (
                <div className={`toast-notification toast-${toast.type}`}>
                    {toast.message}
                </div>
            )}

            {/* Filter Bar */}
            <div className="filter-bar">
                <div className="filter-group">
                    <label>Account Type</label>
                    <select 
                        value={filters.accountType}
                        onChange={(e) => handleFilterChange('accountType', e.target.value)}
                    >
                        <option value="all">All Accounts</option>
                        <option value="super_master">Super Master</option>
                        <option value="master">Master</option>
                        <option value="agent">Agent</option>
                        <option value="user">User</option>
                    </select>
                </div>

                <div className="filter-group">
                    <label>Transaction Type</label>
                    <select 
                        value={filters.transactionType}
                        onChange={(e) => handleFilterChange('transactionType', e.target.value)}
                    >
                        <option value="all">All</option>
                        <option value="deposit">Deposits</option>
                        <option value="withdrawal">Withdrawals</option>
                        <option value="win">Wins</option>
                        <option value="loss">Losses</option>
                    </select>
                </div>

                <div className="filter-group">
                    <label>Search Client</label>
                    <input
                        type="text"
                        placeholder="Username..."
                        value={filters.searchClient}
                        onChange={(e) => handleFilterChange('searchClient', e.target.value)}
                    />
                </div>

                <div className="filter-group">
                    <label>From Date</label>
                    <input
                        type="date"
                        value={filters.fromDate}
                        onChange={(e) => handleFilterChange('fromDate', e.target.value)}
                    />
                </div>

                <div className="filter-group">
                    <label>To Date</label>
                    <input
                        type="date"
                        value={filters.toDate}
                        onChange={(e) => handleFilterChange('toDate', e.target.value)}
                    />
                </div>
            </div>

            {/* Results info */}
            <div className="results-info">
                Showing {paginatedTransactions.length} of {filteredTransactions.length} transactions
                {filters.searchClient && ` for "${filters.searchClient}"`}
            </div>

            {/* Transactions Table */}
            <div className="ast-wrapper">
                <table className="ast-table">
                    <thead>
                        <tr>
                            <th>Username</th>
                            <th>matchName</th>
                            <th>Selection</th>
                            <th>Odds</th>
                            <th>Actual Result</th>
                            <th>Stake</th>
                            <th>Win/Loss</th>
                            <th>Time</th>
                            <th>Running Balance</th>
                            {/* ============ Actions Column - Admin Only ============ */}
                            {isAdmin && <th>Actions</th>}
                        </tr>
                    </thead>
                    <tbody>
                        {paginatedTransactions.map((transaction, index) => {
                            const rowColorClass = getRowColorClass(transaction);
                            const matchName = transaction.betDetails?.matchName || transaction.matchName || '-';
                            const selection = transaction.betDetails?.selectionName || transaction.selectionName || '-';
                            const odds = transaction.betDetails?.odds ?? transaction.odds ?? '-';
                            const actualResult = transaction.betDetails?.actualResult ?? transaction.actualResult ?? '-';
                            const stake = transaction.betDetails?.stake ?? transaction.stake ?? 0;
                            
                            // Check if this is a SETTLED bet transaction
                            const isBetTransaction = !!(transaction.betDetails && transaction.betDetails.result);
                            const betId = transaction.id;

                            return (
                                <tr key={transaction.id || index} className={`ast-row-clickable ${rowColorClass}`}>
                                    <td className="ast-username">{transaction.username || '-'}</td>
                                    <td className="ast-match">{matchName}</td>
                                    <td className="ast-selection">{selection}</td>
                                    <td className="ast-odds">{odds}</td>
                                    <td className="ast-result">{actualResult !== null && actualResult !== undefined ? actualResult : '-'}</td>
                                    <td className="ast-stake">{formatCurrency(Math.abs(stake))}</td>
                                    <td className={transaction.amount >= 0 ? 'ast-credit' : 'ast-debit'}>
                                        {transaction.amount >= 0 ? '+' : '-'}{formatCurrency(Math.abs(transaction.amount))}
                                    </td>
                                    <td className="ast-time">{formatDate(transaction.date)}</td>
                                    <td className="ast-balance">{formatCurrency(transaction.runningBalance)}</td>
                                    
                                    {/* ============ Unsettle Button - Admin Only ============ */}
                                    {isAdmin && (
                                        <td className="ast-actions">
                                            {isBetTransaction && betId ? (
                                                <button
                                                    className="unsettle-btn"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        handleUnsettleClick(transaction);
                                                    }}
                                                    title={`Unsettle bet ${betId}`}
                                                >
                                                    🔄 Unsettle
                                                </button>
                                            ) : (
                                                <span className="no-action">-</span>
                                            )}
                                        </td>
                                    )}
                                </tr>
                            );
                        })}
                        {paginatedTransactions.length === 0 && !loading && (
                            <tr>
                                <td colSpan={isAdmin ? 10 : 9} className="ast-text-center">
                                    {filters.searchClient
                                        ? `No transactions found for "${filters.searchClient}"`
                                        : 'No transactions found'}
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>

                {/* Pagination */}
                {pagination.totalPages > 1 && (
                    <div className="ast-pagination">
                        <button
                            onClick={() => handlePageChange(pagination.currentPage - 1)}
                            disabled={pagination.currentPage === 1}
                            className="ast-page-btn"
                        >
                            ◀ Previous
                        </button>
                        <div className="ast-page-info">
                            Page {pagination.currentPage} of {pagination.totalPages}
                            <span className="ast-total-items">
                                ({filteredTransactions.length} total)
                            </span>
                        </div>
                        <button
                            onClick={() => handlePageChange(pagination.currentPage + 1)}
                            disabled={pagination.currentPage === pagination.totalPages}
                            className="ast-page-btn"
                        >
                            Next ▶
                        </button>
                    </div>
                )}
            </div>

            {/* Transaction Details Modal */}
            {showBetModal && selectedTransaction && (
                <div className="modal-overlay" onClick={() => setShowBetModal(false)}>
                    <div className="modal bet-details-modal" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header">
                            <h2>Transaction Details</h2>
                            <button className="close-btn" onClick={() => setShowBetModal(false)}>✕</button>
                        </div>
                        
                        <div className="modal-body">
                            <div className="details-section">
                                <h3>Basic Information</h3>
                                <div className="details-grid">
                                    <div className="detail-item">
                                        <label>Bet ID:</label>
                                        <span className="bet-id-display">{selectedTransaction.id || 'N/A'}</span>
                                    </div>
                                    <div className="detail-item">
                                        <label>Username:</label>
                                        <span>{selectedTransaction.username || '-'}</span>
                                    </div>
                                    <div className="detail-item">
                                        <label>Date & Time:</label>
                                        <span>{formatDate(selectedTransaction.date)}</span>
                                    </div>
                                    <div className="detail-item">
                                        <label>Transaction Type:</label>
                                        <span className={`type-badge ${selectedTransaction.type}`}>
                                            {selectedTransaction.type?.toUpperCase() || 'N/A'}
                                        </span>
                                    </div>
                                    <div className="detail-item">
                                        <label>Amount:</label>
                                        <span className={selectedTransaction.amount >= 0 ? 'credit-amount' : 'debit-amount'}>
                                            {selectedTransaction.amount >= 0 ? '+' : ''}{formatCurrency(Math.abs(selectedTransaction.amount || 0))}
                                        </span>
                                    </div>
                                    <div className="detail-item">
                                        <label>Balance After:</label>
                                        <span>{formatCurrency(selectedTransaction.runningBalance || 0)}</span>
                                    </div>
                                </div>
                            </div>

                            {selectedTransaction.betDetails && (
                                <div className="details-section">
                                    <h3>Bet Details</h3>
                                    <div className="details-grid">
                                        <div className="detail-item">
                                            <label>Bet ID:</label>
                                            <span className="bet-id-display">{selectedTransaction.id || 'N/A'}</span>
                                        </div>
                                        <div className="detail-item">
                                            <label>Match:</label>
                                            <span className="match-name">
                                                🏏 {selectedTransaction.betDetails.matchName || 'N/A'}
                                            </span>
                                        </div>
                                        <div className="detail-item">
                                            <label>Selection:</label>
                                            <span>{selectedTransaction.betDetails.selectionName || 'N/A'}</span>
                                        </div>
                                        <div className="detail-item">
                                            <label>Market Type:</label>
                                            <span>{selectedTransaction.betDetails.marketType?.toUpperCase() || 'N/A'}</span>
                                        </div>
                                        <div className="detail-item">
                                            <label>Bet Type:</label>
                                            <span className={`bet-type-badge ${getBetTypeClass(selectedTransaction.betDetails)}`}>
                                                {getBetTypeDisplay(selectedTransaction.betDetails) || 'N/A'}
                                            </span>
                                        </div>
                                        <div className="detail-item">
                                            <label>Odds:</label>
                                            <span>{selectedTransaction.betDetails.odds || 'N/A'}</span>
                                        </div>
                                        <div className="detail-item">
                                            <label>Stake:</label>
                                            <span>{formatCurrency(selectedTransaction.betDetails.stake || 0)}</span>
                                        </div>
                                        <div className="detail-item">
                                            <label>Potential Win:</label>
                                            <span className="profit">{formatCurrency(selectedTransaction.betDetails.potentialWin || 0)}</span>
                                        </div>
                                        {selectedTransaction.betDetails.actualResult !== undefined && selectedTransaction.betDetails.actualResult !== null && (
                                            <div className="detail-item">
                                                <label>Actual Result:</label>
                                                <span>{selectedTransaction.betDetails.actualResult}</span>
                                            </div>
                                        )}
                                        {selectedTransaction.betDetails.targetValue && (
                                            <div className="detail-item">
                                                <label>Target:</label>
                                                <span>{selectedTransaction.betDetails.comparison || 'none'} {selectedTransaction.betDetails.targetValue}</span>
                                            </div>
                                        )}
                                        <div className="detail-item">
                                            <label>Result:</label>
                                            <span className={`result-badge ${selectedTransaction.betDetails.result}`}>
                                                {selectedTransaction.betDetails.result?.toUpperCase() || 'N/A'}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            )}

                            <div className="detail-item full-width">
                                <label>Description:</label>
                                <div className="remarks-box">
                                    {selectedTransaction.description || 'N/A'}
                                </div>
                            </div>
                        </div>

                        <div className="modal-footer">
                            {isAdmin && selectedTransaction.id && selectedTransaction.betDetails && 
                                selectedTransaction.betDetails.result && (
                                <button 
                                    className="unsettle-modal-btn"
                                    onClick={() => handleUnsettleClick(selectedTransaction)}
                                >
                                    🔄 Unsettle Bet
                                </button>
                            )}
                            <button onClick={() => setShowBetModal(false)} className="close-modal-btn">
                                Close
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Unsettle Confirmation Modal */}
            {showUnsettleModal && unsettlingBet && (
                <div className="modal-overlay" onClick={() => setShowUnsettleModal(false)}>
                    <div className="modal unsettle-modal" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header">
                            <h2>⚠️ Confirm Unsettle</h2>
                            <button className="close-btn" onClick={() => setShowUnsettleModal(false)}>✕</button>
                        </div>
                        
                        <div className="modal-body">
                     

                            <div className="details-section">
                                <h3>Bet Information</h3>
                                <div className="details-grid">
                                    <div className="detail-item">
                                        <label>Bet ID:</label>
                                        <span className="bet-id-display">{unsettlingBet.id || 'N/A'}</span>
                                    </div>
                                    <div className="detail-item">
                                        <label>Username:</label>
                                        <span>{unsettlingBet.username || '-'}</span>
                                    </div>
                                    <div className="detail-item">
                                        <label>Match:</label>
                                        <span>{unsettlingBet.betDetails?.matchName || 'N/A'}</span>
                                    </div>
                                    <div className="detail-item">
                                        <label>Selection:</label>
                                        <span>{unsettlingBet.betDetails?.selectionName || 'N/A'}</span>
                                    </div>
                                    <div className="detail-item">
                                        <label>Current Status:</label>
                                        <span className={`result-badge ${unsettlingBet.betDetails?.result}`}>
                                            {unsettlingBet.betDetails?.result?.toUpperCase() || unsettlingBet.type?.toUpperCase() || 'N/A'}
                                        </span>
                                    </div>
                                    <div className="detail-item">
                                        <label>Amount:</label>
                                        <span className={unsettlingBet.amount >= 0 ? 'credit-amount' : 'debit-amount'}>
                                            {unsettlingBet.amount >= 0 ? '+' : ''}{formatCurrency(Math.abs(unsettlingBet.amount || 0))}
                                        </span>
                                    </div>
                                </div>
                            </div>
                            

    
                        </div>
                        
                        <div className="modal-footer">
                            <button 
                                onClick={() => setShowUnsettleModal(false)} 
                                className="close-modal-btn"
                                disabled={unsettling}
                            >
                                Cancel
                            </button>
                            <button 
                                onClick={handleConfirmUnsettle} 
                                className="unsettle-confirm-btn"
                                disabled={unsettling}
                            >
                                {unsettling ? 'Unsettling...' : '✅ Confirm Unsettle'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AccountStatement;